import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import { Configuration, ImageApi, LearningApi } from "../../api/news";
import { getUserKeyLocalStorage } from "../../api/LocalEnvironmentClient";
import RecipeCreatorWidget from "../../components/widget/RecipeCreatorWidget";
import GarminAssistantWidget from "../../components/widget/GarminAssistantWidget";

// Define the API base URL from the OpenAPI spec
const API_BASE_URL =
  "https://gkz1ukftsi.execute-api.us-west-1.amazonaws.com/prod/";
const CHECKINS_API_BASE_URL = API_BASE_URL.replace(/\/$/, "");

// Alert timeout duration in milliseconds (4 seconds)
const ALERT_TIMEOUT = 4000;

type CheckinFormState = {
  checkin_date: string;
  day_rating: string;
  mood: string;
  energy: string;
  stress: string;
  focus_quality: string;
  sleep_quality: string;
  biggest_win: string;
  biggest_blocker: string;
  journal_note: string;
};

type CheckinResponse = {
  checkin_date: string;
  day_rating?: number | null;
  mood?: number | null;
  energy?: number | null;
  stress?: number | null;
  focus_quality?: number | null;
  sleep_quality?: number | null;
  biggest_win?: string | null;
  biggest_blocker?: string | null;
  journal_note?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const buildEmptyCheckinForm = (): CheckinFormState => ({
  checkin_date: dayjs().format("YYYY-MM-DD"),
  day_rating: "",
  mood: "",
  energy: "",
  stress: "",
  focus_quality: "",
  sleep_quality: "",
  biggest_win: "",
  biggest_blocker: "",
  journal_note: "",
});

const mapCheckinToForm = (
  checkin?: CheckinResponse | null
): CheckinFormState => ({
  checkin_date: checkin?.checkin_date || dayjs().format("YYYY-MM-DD"),
  day_rating:
    checkin?.day_rating === undefined || checkin?.day_rating === null
      ? ""
      : String(checkin.day_rating),
  mood:
    checkin?.mood === undefined || checkin?.mood === null
      ? ""
      : String(checkin.mood),
  energy:
    checkin?.energy === undefined || checkin?.energy === null
      ? ""
      : String(checkin.energy),
  stress:
    checkin?.stress === undefined || checkin?.stress === null
      ? ""
      : String(checkin.stress),
  focus_quality:
    checkin?.focus_quality === undefined || checkin?.focus_quality === null
      ? ""
      : String(checkin.focus_quality),
  sleep_quality:
    checkin?.sleep_quality === undefined || checkin?.sleep_quality === null
      ? ""
      : String(checkin.sleep_quality),
  biggest_win: checkin?.biggest_win || "",
  biggest_blocker: checkin?.biggest_blocker || "",
  journal_note: checkin?.journal_note || "",
});

const ratingOptions = [
  { value: "1", label: "1" },
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
  { value: "5", label: "5" },
];

export default function AdminPage({ isAuthorized }: { isAuthorized: boolean }) {
  const [learningTopic, setLearningTopic] = useState("");
  const [learningTopicAlert, setLearningTopicAlert] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const [promptTitle, setPromptTitle] = useState("");
  const [promptText, setPromptText] = useState("");
  const [imagePromptAlert, setImagePromptAlert] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const [checkinForm, setCheckinForm] = useState<CheckinFormState>(
    buildEmptyCheckinForm()
  );
  const [checkinHistory, setCheckinHistory] = useState<CheckinResponse[]>([]);
  const [checkinAlert, setCheckinAlert] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const [isLoadingCheckin, setIsLoadingCheckin] = useState(false);
  const [isSavingCheckin, setIsSavingCheckin] = useState(false);

  const config = new Configuration({
    basePath: API_BASE_URL,
  });
  const learningApi = new LearningApi(config);
  const imageApi = new ImageApi(config);
  const adminToken = getUserKeyLocalStorage();
  const authHeaders = {
    Authorization: `Bearer ${adminToken}`,
  };

  const loadCheckinData = async () => {
    setIsLoadingCheckin(true);
    try {
      const [todayResponse, historyResponse] = await Promise.all([
        axios.get<CheckinResponse | null>(`${CHECKINS_API_BASE_URL}/checkins`, {
          headers: authHeaders,
        }),
        axios.get<CheckinResponse[]>(
          `${CHECKINS_API_BASE_URL}/checkins/history`,
          {
            headers: authHeaders,
            params: { limit: 7 },
          }
        ),
      ]);

      setCheckinForm(mapCheckinToForm(todayResponse.data));
      setCheckinHistory(historyResponse.data || []);
    } catch (error: any) {
      setCheckinAlert({
        message:
          error.response?.data?.error || "Error loading daily check-in data.",
        severity: "error",
      });
    } finally {
      setIsLoadingCheckin(false);
    }
  };

  const handleCheckinFieldChange =
    (field: keyof CheckinFormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCheckinForm((previousState) => ({
        ...previousState,
        [field]: event.target.value,
      }));
    };

  const saveCheckin = async () => {
    setIsSavingCheckin(true);
    try {
      const payload = Object.entries(checkinForm).reduce(
        (accumulator, [key, value]) => {
          if (value !== "") {
            accumulator[key] = value;
          }
          return accumulator;
        },
        {} as Record<string, string>
      );

      const response = await axios.post<CheckinResponse>(
        `${CHECKINS_API_BASE_URL}/checkins`,
        payload,
        {
          headers: authHeaders,
        }
      );
      setCheckinForm(mapCheckinToForm(response.data));
      setCheckinAlert({
        message: "Daily check-in saved.",
        severity: "success",
      });
      loadCheckinData();
    } catch (error: any) {
      setCheckinAlert({
        message: error.response?.data?.error || "Error saving daily check-in.",
        severity: "error",
      });
    } finally {
      setIsSavingCheckin(false);
    }
  };

  // Function to add a LearningTopic
  const addLearningTopic = async () => {
    if (!learningTopic.trim()) {
      setLearningTopicAlert({
        message: "Please enter a learning topic.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await learningApi.addLearningTopic({
        topic: learningTopic,
      });
      setLearningTopicAlert({
        message: response.data.message || "Learning topic added successfully.",
        severity: "success",
      });
      setLearningTopic(""); // Clear input on success
    } catch (error: any) {
      setLearningTopicAlert({
        message:
          error.response?.data?.message || "Error adding learning topic.",
        severity: "error",
      });
    }
  };

  // Function to add an ImageOfTheDayPrompt
  const addImagePrompt = async () => {
    if (!promptTitle.trim() || !promptText.trim()) {
      setImagePromptAlert({
        message: "Please enter both a title and text.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await imageApi.addImagePrompt({
        title: promptTitle,
        text: promptText,
      });
      setImagePromptAlert({
        message: response.data.message || "Image prompt added successfully.",
        severity: "success",
      });
      setPromptTitle(""); // Clear inputs on success
      setPromptText("");
    } catch (error: any) {
      setImagePromptAlert({
        message: error.response?.data?.message || "Error adding image prompt.",
        severity: "error",
      });
    }
  };

  // Effect to clear learning topic alert after timeout
  useEffect(() => {
    if (learningTopicAlert) {
      const timer = setTimeout(
        () => setLearningTopicAlert(null),
        ALERT_TIMEOUT
      );
      return () => clearTimeout(timer); // Cleanup on unmount or alert change
    }
  }, [learningTopicAlert]);

  // Effect to clear image prompt alert after timeout
  useEffect(() => {
    if (imagePromptAlert) {
      const timer = setTimeout(() => setImagePromptAlert(null), ALERT_TIMEOUT);
      return () => clearTimeout(timer); // Cleanup on unmount or alert change
    }
  }, [imagePromptAlert]);

  useEffect(() => {
    if (checkinAlert) {
      const timer = setTimeout(() => setCheckinAlert(null), ALERT_TIMEOUT);
      return () => clearTimeout(timer);
    }
  }, [checkinAlert]);

  useEffect(() => {
    if (isAuthorized) {
      loadCheckinData();
    }
  }, [isAuthorized]);

  // Render unauthorized message if not authorized
  if (!isAuthorized) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h3">Unauthorized</Typography>
              <Typography variant="body1">
                You need administrative access to view this page.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    );
  }

  // Render the admin dashboard
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h3">Admin Dashboard</Typography>
            <Typography variant="body1">
              Manage daily check-ins, learning topics, recipes, image prompts,
              and Garmin tools here.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5">Daily Check-In</Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              One entry per day. Open this any time, update it once, and revise
              it later if needed.
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Check-In Date"
                  type="date"
                  value={checkinForm.checkin_date}
                  onChange={handleCheckinFieldChange("checkin_date")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {[
                ["day_rating", "Overall Day Rating"],
                ["mood", "Mood"],
                ["energy", "Energy"],
                ["stress", "Stress"],
                ["focus_quality", "Focus Quality"],
                ["sleep_quality", "Sleep Quality"],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={6} key={field}>
                  <TextField
                    select
                    label={label}
                    value={checkinForm[field as keyof CheckinFormState]}
                    onChange={handleCheckinFieldChange(
                      field as keyof CheckinFormState
                    )}
                    fullWidth
                  >
                    <MenuItem value="">Not set</MenuItem>
                    {ratingOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}

              <Grid item xs={12}>
                <TextField
                  label="Biggest Win"
                  value={checkinForm.biggest_win}
                  onChange={handleCheckinFieldChange("biggest_win")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Biggest Blocker"
                  value={checkinForm.biggest_blocker}
                  onChange={handleCheckinFieldChange("biggest_blocker")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Journal Note"
                  value={checkinForm.journal_note}
                  onChange={handleCheckinFieldChange("journal_note")}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3, mb: 2 }}>
              <Button
                variant="contained"
                onClick={saveCheckin}
                disabled={isSavingCheckin || isLoadingCheckin}
              >
                {isSavingCheckin ? "Saving..." : "Save Check-In"}
              </Button>
              <Button
                variant="outlined"
                onClick={loadCheckinData}
                disabled={isSavingCheckin || isLoadingCheckin}
              >
                {isLoadingCheckin ? "Refreshing..." : "Refresh"}
              </Button>
            </Box>

            {checkinAlert && (
              <Alert sx={{ mt: 2 }} severity={checkinAlert.severity}>
                {checkinAlert.message}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Recent Check-Ins
            </Typography>
            {checkinHistory.length === 0 ? (
              <Typography variant="body2">
                No recent check-ins saved yet.
              </Typography>
            ) : (
              checkinHistory.map((entry) => (
                <Paper
                  key={entry.checkin_date}
                  variant="outlined"
                  sx={{ p: 2, mb: 2 }}
                >
                  <Typography variant="subtitle1">
                    {entry.checkin_date}
                  </Typography>
                  <Typography variant="body2">
                    Day {entry.day_rating || "-"} | Mood {entry.mood || "-"} |
                    Energy {entry.energy || "-"} | Stress {entry.stress || "-"}|
                    Focus {entry.focus_quality || "-"} | Sleep{" "}
                    {entry.sleep_quality || "-"}
                  </Typography>
                  {entry.biggest_win && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Win: {entry.biggest_win}
                    </Typography>
                  )}
                  {entry.biggest_blocker && (
                    <Typography variant="body2">
                      Blocker: {entry.biggest_blocker}
                    </Typography>
                  )}
                  {entry.journal_note && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {entry.journal_note}
                    </Typography>
                  )}
                </Paper>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <RecipeCreatorWidget />
        </Grid>

        <Grid item xs={12} md={8}>
          <GarminAssistantWidget />
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 4 }}>
            {/* Learning Topic Section */}
            <Typography variant="h5">Add Learning Topic</Typography>
            <TextField
              label="Learning Topic"
              value={learningTopic}
              onChange={(e) => setLearningTopic(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={addLearningTopic}>
              Add Topic
            </Button>
            {learningTopicAlert && (
              <Alert sx={{ mt: 2 }} severity={learningTopicAlert.severity}>
                {learningTopicAlert.message}
              </Alert>
            )}

            {/* Image Prompt Section */}
            <Typography variant="h5" sx={{ mt: 4 }}>
              Add Image Prompt
            </Typography>
            <TextField
              label="Prompt Title"
              value={promptTitle}
              onChange={(e) => setPromptTitle(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Prompt Text"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              fullWidth
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" onClick={addImagePrompt}>
              Add Prompt
            </Button>
            {imagePromptAlert && (
              <Alert sx={{ mt: 2 }} severity={imagePromptAlert.severity}>
                {imagePromptAlert.message}
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
